import axiosApi from "../../../../../../utils/axios";

const UPLOAD_CONCURRENCY = 3;
const MAX_ATTEMPTS = 5;
// Retrying cannot fix these: the session is gone, a restore started or access was denied
const FATAL_STATUSES = [401, 403, 404, 409];

const abortError = () => new DOMException("Aborted", "AbortError");

export class UploadPaused extends Error {
  constructor() {
    super("Upload paused");
    this.name = "UploadPaused";
  }
}

// Resolves after ms, or rejects as soon as one of the signals aborts
const sleep = (ms, signals) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signals.forEach((signal) =>
      signal?.addEventListener(
        "abort",
        () => {
          clearTimeout(timer);
          reject(abortError());
        },
        { once: true },
      ),
    );
  });

const toHex = (buffer) =>
  Array.from(new Uint8Array(buffer), (byte) => byte.toString(16).padStart(2, "0")).join("");

// crypto.subtle only exists in secure contexts (HTTPS); without it the server skips the check
const sha256Hex = async (blob) =>
  window.crypto?.subtle
    ? toHex(await window.crypto.subtle.digest("SHA-256", await blob.arrayBuffer()))
    : null;

export const formatSize = (bytes) => {
  if (bytes === null || bytes === undefined) return "";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
};

export const formatDuration = (seconds) => {
  if (!Number.isFinite(seconds)) return "";
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours) return `${hours}h ${minutes}m`;
  if (minutes) return `${minutes}m`;
  return `${Math.max(1, Math.round(seconds))}s`;
};

// "running for 1h 12m"; empty when the start time is unknown
export const runningFor = (startedAt) => {
  const seconds = (Date.now() - Date.parse(startedAt)) / 1000;
  return startedAt && seconds > 0 ? `running for ${formatDuration(seconds)}` : "";
};

export const isSameFile = (file, upload) =>
  Boolean(
    file &&
    upload &&
    file.name === upload.fileName &&
    file.size === upload.size &&
    file.lastModified === upload.lastModified,
  );

/**
 * Uploads the chunks the server does not have yet. Every chunk is written at its own
 * offset on the server, so chunks can be retried independently and an interrupted upload
 * continues where it stopped.
 *
 * onProgress receives { sent, saved }: sent includes the chunks still being sent, saved
 * only the chunks the server has stored (where an interrupted upload continues from).
 * Aborting pauseSignal stops taking new chunks but lets the ones being sent finish, so
 * nothing already shown as uploaded is lost; it then rejects with UploadPaused.
 * Aborting signal cancels the requests immediately.
 */
export const uploadMissingChunks = async ({ file, upload, onProgress, signal, pauseSignal }) => {
  const { id, chunkSize, totalChunks } = upload;
  const received = new Set(upload.received || []);
  const chunkLength = (index) => Math.min(chunkSize, file.size - index * chunkSize);

  const queue = [];
  for (let index = 0; index < totalChunks; index++) {
    if (!received.has(index)) queue.push(index);
  }

  let doneBytes = [...received].reduce((sum, index) => sum + chunkLength(index), 0);
  const inFlight = new Map();
  const report = () =>
    onProgress({
      saved: doneBytes,
      sent: doneBytes + [...inFlight.values()].reduce((sum, loaded) => sum + loaded, 0),
    });
  report();

  // Cancels the requests in flight: on a hard abort or when one chunk fails for good
  const controller = new AbortController();
  const stop = () => controller.abort();
  signal.addEventListener("abort", stop, { once: true });

  const uploadChunk = async (index) => {
    const start = index * chunkSize;
    const blob = file.slice(start, start + chunkLength(index));
    const checksum = await sha256Hex(blob);

    for (let attempt = 1; ; attempt++) {
      try {
        await axiosApi.put(`/api/settings/restore/uploads/${id}/chunks/${index}`, blob, {
          headers: {
            "Content-Type": "application/octet-stream",
            ...(checksum && { "X-Chunk-SHA256": checksum }),
          },
          signal: controller.signal,
          onUploadProgress: (event) => {
            inFlight.set(index, Math.min(event.loaded, blob.size));
            report();
          },
        });
        inFlight.delete(index);
        doneBytes += blob.size;
        report();
        return;
      } catch (error) {
        inFlight.delete(index);
        report();
        const status = error?.response?.status;
        if (controller.signal.aborted || FATAL_STATUSES.includes(status) || attempt >= MAX_ATTEMPTS)
          throw error;
        // A failing chunk is not retried once paused; it is sent again on resume
        if (pauseSignal?.aborted) throw new UploadPaused();
        try {
          await sleep(1000 * 2 ** (attempt - 1), [controller.signal, pauseSignal]);
        } catch (sleepError) {
          if (!controller.signal.aborted && pauseSignal?.aborted) throw new UploadPaused();
          throw sleepError;
        }
      }
    }
  };

  const worker = async () => {
    while (queue.length && !pauseSignal?.aborted) {
      if (controller.signal.aborted) throw abortError();
      await uploadChunk(queue.shift());
    }
  };

  let firstError = null;
  try {
    // allSettled: a pause must wait for the chunks still being sent
    await Promise.allSettled(
      Array.from({ length: Math.min(UPLOAD_CONCURRENCY, queue.length) }, () =>
        worker().catch((error) => {
          if (error instanceof UploadPaused) return;
          if (!firstError) firstError = error;
          // Stops the other workers as soon as one chunk fails for good
          stop();
        }),
      ),
    );
  } finally {
    signal.removeEventListener("abort", stop);
  }
  if (firstError) throw firstError;
  if (pauseSignal?.aborted) throw new UploadPaused();
};
